import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { PricingPartnerState } from '@app/modules/pricing/store/pricing-partner.state';
import { Partner } from '@core/core.types';
import { momentToNgbDate } from '@core/utils/core.util';
import {
  NgbActiveModal,
  NgbDatepickerModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { PartnerTypeaheadComponent } from '@shared/components/partner-typeahead/partner-typeahead.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import moment from 'moment';
import { finalize, map, Observable, of, switchMap } from 'rxjs';
import { PricingCountryService } from '../../../services/pricing-country.service';
import { PricingPartnerService } from '../../../services/pricing-partner.service';
import { AddPricingPartnerBundleAction } from '../../../store/pricing-partner.actions';
import {
  CountryBundlePrice,
  CountryPriceType,
  PricingPartner,
} from '../../../types/pricing.types';

interface BulkPriceRow {
  bundleId: string;
  name: string;
  currentPrice: number | null;
  priceControl: FormControl<number>;
}

@Component({
  selector: 'app-bulk-price-dialog',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NgbDatepickerModule,
    NgbTooltipModule,
    PartnerTypeaheadComponent,
  ],
  templateUrl: './bulk-price-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bulk-price-dialog.component.scss',
})
export class BulkPriceDialogComponent {
  public partnerId = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private pricingPartnerService = inject(PricingPartnerService);
  private pricingCountryService = inject(PricingCountryService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  protected pricingPartner = computed(() =>
    this.store.selectSignal(PricingPartnerState.getPricingPartnerById)()(
      this.partnerId()
    )
  );
  protected currencyMissing = computed(() => !this.pricingPartner()?.currency);

  protected validFromControl = new FormControl<moment.Moment>(
    null,
    Validators.required
  );
  protected rows = signal<BulkPriceRow[]>([]);
  protected copyPartnerControl = new FormControl<string>(null);
  protected copyingFromCountry = signal<boolean>(false);

  protected minDate = moment().add(1, 'day');
  protected maxDate = moment().add(100, 'year');
  protected startDate = moment().add(1, 'month');

  constructor() {
    effect(() => {
      this.refreshRows();
    });
  }

  protected refreshRows() {
    const partner = this.pricingPartner();
    const rows =
      partner?.pricelist?.map((bundle) => ({
        bundleId: bundle.id,
        name: bundle.internalName,
        currentPrice: bundle.pivot?.price ?? null,
        priceControl: new FormControl<number>(bundle.pivot?.price ?? null, [
          Validators.min(1),
        ]),
      })) ?? [];

    this.rows.set(
      rows.sort((a, b) =>
        a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
      )
    );
  }

  protected submitDisabled() {
    if (this.currencyMissing()) {
      return true;
    }

    if (!this.validFromControl.value) {
      return true;
    }

    if (!this.rows().length) {
      return true;
    }

    return !this.hasChanges();
  }

  protected hasChanges() {
    return this.rows().some((row) => {
      const value = row.priceControl.value;
      if (value === null || value === undefined) {
        return false;
      }
      return Number(value) !== Number(row.currentPrice);
    });
  }

  protected save() {
    if (this.submitDisabled()) {
      return;
    }

    const validFrom = this.validFromControl.value?.toDate() || null;
    const actions = this.rows()
      .filter((row) => {
        const value = row.priceControl.value;
        if (value === null || value === undefined) {
          return false;
        }
        return Number(value) !== Number(row.currentPrice);
      })
      .map(
        (row) =>
          new AddPricingPartnerBundleAction(
            this.partnerId(),
            row.bundleId,
            Number(row.priceControl.value),
            validFrom
          )
      );

    if (!actions.length) {
      this.modalRef.close();
      return;
    }

    this.store.dispatch(actions).subscribe(() => {
      this.modalRef.close();
    });
  }

  protected onPartnerSelected(partner: Partner) {
    if (!partner) {
      return;
    }
    this.pricingPartnerService.getPricingPartner(partner.id).subscribe({
      next: (pricingPartner) => {
        this.applyPartnerPrices(pricingPartner);
      },
    });
  }

  /**
   * Copies the WSP prices of the partner's country (countries tab) into the
   * "new price" inputs, matched by bundle.
   */
  protected copyFromCountry(event: MouseEvent) {
    event.preventDefault();

    const countryName = this.pricingPartner()?.country;

    if (!countryName) {
      this.toastService.warning(
        this.translateService.instant(
          'pricing.partner.details.bulkEdit.copyFromCountryNoCountry'
        )
      );
      return;
    }

    this.copyingFromCountry.set(true);

    this.pricingCountryService
      .getPricingCountriesList({ name: countryName })
      .pipe(
        map((countries) =>
          countries.data.find(
            (country) =>
              country.name.toLowerCase() === countryName.toLowerCase()
          )
        ),
        switchMap(
          (country): Observable<CountryBundlePrice[] | null> =>
            country
              ? this.pricingCountryService.getPricingCountryBundles(
                  country.id,
                  CountryPriceType.WSP
                )
              : of(null)
        ),
        finalize(() => this.copyingFromCountry.set(false))
      )
      .subscribe({
        next: (bundles) => {
          if (!bundles) {
            this.toastService.warning(
              this.translateService.instant(
                'pricing.partner.details.bulkEdit.copyFromCountryNotFound',
                { country: countryName }
              )
            );
            return;
          }

          const copied = this.applyCountryPrices(bundles);

          if (!copied) {
            this.toastService.info(
              this.translateService.instant(
                'pricing.partner.details.bulkEdit.copyFromCountryNoPrices',
                { country: countryName }
              )
            );
          }
        },
        error: (error) => {
          this.toastService.danger(error.error?.message, error.error?.error);
        },
      });
  }

  private applyCountryPrices(bundles: CountryBundlePrice[]): number {
    const priceByBundleId = new Map(
      bundles
        .filter(
          (bundle) =>
            bundle?.pivot?.price !== null && bundle?.pivot?.price !== undefined
        )
        .map((bundle) => [bundle.id, bundle.pivot.price])
    );

    let copied = 0;

    this.rows().forEach((row) => {
      if (priceByBundleId.has(row.bundleId)) {
        row.priceControl.setValue(priceByBundleId.get(row.bundleId));
        copied++;
      }
    });

    return copied;
  }

  private applyPartnerPrices(pricingPartner: PricingPartner) {
    if (!pricingPartner?.pricelist?.length) {
      return;
    }

    const priceByBundleId = new Map(
      pricingPartner.pricelist
        .filter((bundle) => bundle?.pivot?.price !== null)
        .map((bundle) => [bundle.id, bundle.pivot.price])
    );

    this.rows().forEach((row) => {
      if (priceByBundleId.has(row.bundleId)) {
        row.priceControl.setValue(priceByBundleId.get(row.bundleId));
      }
    });
  }

  protected readonly momentToNgbDate = momentToNgbDate;
}
